import { useState, useEffect } from 'react';
import { createAPI } from 'edge-stream-js';
import { useToast } from 'edge-stream-js-react';
import { ErrorBoundary } from './components';
import {
  InfoIcon, BuildingIcon, ChatIcon, CopyIcon, CheckIcon, SuccessIcon,
  WindowIcon, LightbulbIcon, DemoSetupIcon, OneClickIcon, ManualSetupIcon,
} from '@bizfirst/common-react';

const DEMO_TAB_STORAGE_KEY = 'edgestream-demo-tab';

interface DemoPageProps {
  apiBaseUrl: string;
  accessToken?: string;
}

interface DemoServer {
  name: string;
  serverId: string;
  groups: Array<{ name: string; description: string }>;
  color: string;
}

const DEMO_SERVERS: DemoServer[] = [
  {
    name: 'BAS Server',
    serverId: 'bas-server-1',
    groups: [
      { name: 'general', description: 'General business activities' },
      { name: 'sales', description: 'Sales team activities' },
      { name: 'support', description: 'Customer support activities' },
    ],
    color: '#00d4ff',
  },
  {
    name: 'Chat Server',
    serverId: 'chat-server-1',
    groups: [
      { name: 'announcements', description: 'Company announcements' },
      { name: 'random', description: 'Off-topic discussions' },
      { name: 'engineering', description: 'Engineering team chat' },
    ],
    color: '#3fb950',
  },
  {
    name: 'ERP Server',
    serverId: 'erp-server-1',
    groups: [
      { name: 'accounting', description: 'Accounting department' },
      { name: 'inventory', description: 'Inventory management' },
      { name: 'hr', description: 'Human resources' },
    ],
    color: '#f0883e',
  },
];

type DemoTab = 'multi-window' | 'one-click' | 'manual' | 'about';

function getSavedTab(): DemoTab {
  try {
    const saved = localStorage.getItem(DEMO_TAB_STORAGE_KEY);
    if (saved && ['multi-window', 'one-click', 'manual', 'about'].includes(saved)) {
      return saved as DemoTab;
    }
  } catch {}
  return 'one-click';
}

function saveTab(tab: DemoTab): void {
  try {
    localStorage.setItem(DEMO_TAB_STORAGE_KEY, tab);
  } catch {}
}

export function DemoPage({ apiBaseUrl, accessToken }: DemoPageProps) {
  const api = createAPI(apiBaseUrl, accessToken ? () => accessToken : undefined);
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [createdServers, setCreatedServers] = useState<string[]>([]);
  const [numberOfWindows, setNumberOfWindows] = useState(2);
  const [showWindows, setShowWindows] = useState(false);
  const [activeTab, setActiveTab] = useState<DemoTab>(getSavedTab());

  useEffect(() => {
    saveTab(activeTab);
  }, [activeTab]);

  const handleCreateDemoServers = async () => {
    setLoading(true);
    const created: string[] = [];

    try {
      for (const server of DEMO_SERVERS) {
        for (const group of server.groups) {
          await api.createGroup(server.serverId, group.name, group.description, 'demo');
          created.push(`${server.serverId}/${group.name}`);
        }
      }

      setCreatedServers(created);
      toast.success(`Created ${created.length} groups across ${DEMO_SERVERS.length} servers!`);
    } catch (error) {
      toast.error(`Error creating demo servers: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleOpenMultipleWindows = () => {
    setShowWindows(true);
    toast.success(`Opened ${numberOfWindows} chat window${numberOfWindows !== 1 ? 's' : ''}!`);
  };

  // Render multi-window view if enabled
  if (showWindows) {
    const appUrl = window.location.origin + window.location.pathname.replace('/demo', '');
    const columns = numberOfWindows <= 2 ? numberOfWindows : numberOfWindows <= 4 ? 2 : 3;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg-dark)',
          zIndex: 9999,
          overflow: 'hidden',
        }}
      >
        {/* Header Bar */}
        <div
          style={{
            padding: '12px 20px',
            background: 'var(--bg-secondary)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={() => setShowWindows(false)}
              className="button button-secondary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                padding: '6px 12px',
              }}
            >
              ← Back
            </button>
            <div>
              <h2 style={{ margin: '0', color: 'var(--primary)', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <WindowIcon /> Multi-Window Chat Testing
              </h2>
              <p style={{ margin: '0', color: 'var(--text-secondary)', fontSize: '12px' }}>
                {numberOfWindows} window{numberOfWindows !== 1 ? 's' : ''} • Click back button to exit
              </p>
            </div>
          </div>
        </div>

        {/* Windows Grid - Full Screen */}
        <div
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: '8px',
            padding: '8px',
            overflow: 'hidden',
          }}
        >
          {Array.from({ length: numberOfWindows }, (_, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '6px',
                overflow: 'hidden',
                border: '1px solid var(--border)',
                background: 'var(--bg-secondary)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                minHeight: '0',
              }}
            >
              {/* Window Title */}
              <div
                style={{
                  padding: '8px 12px',
                  background: 'var(--bg-tertiary)',
                  borderBottom: '1px solid var(--border)',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--primary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  flexShrink: 0,
                }}
              >
                <WindowIcon />
                <span>Window {i + 1}</span>
              </div>

              {/* iframe */}
              <iframe
                key={`${appUrl}-${i}`}
                src={appUrl}
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'var(--bg-dark)',
                  minHeight: '0',
                  width: '100%',
                }}
                title={`Chat Window ${i + 1}`}
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation-by-user-activation allow-modals"
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="page-container">
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'var(--accent-1)', marginBottom: '16px' }}>
            <DemoSetupIcon />
            Demo Setup
        </h2>
        <p className="page-description" style={{ marginBottom: '20px' }}>
          Quickly populate your EdgeStream Inspector with sample servers and groups for testing.
        </p>

        {/* Tab Navigation */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            paddingBottom: '10px',
            borderBottom: '1px solid var(--border)',
            marginBottom: '20px',
          }}
        >
          <button
            onClick={() => setActiveTab('one-click')}
            style={{
              height: '36px',
              padding: '0 16px',
              background: activeTab === 'one-click' ? 'var(--primary)' : 'transparent',
              color: activeTab === 'one-click' ? 'white' : 'var(--text-secondary)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: activeTab === 'one-click' ? 600 : 500,
              fontSize: '13px',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
              gap: '6px',
            }}
          >
            <OneClickIcon /> One-Click Setup
          </button>
          <button
            onClick={() => setActiveTab('multi-window')}
            style={{
              height: '36px',
              padding: '0 16px',
              background: activeTab === 'multi-window' ? 'var(--primary)' : 'transparent',
              color: activeTab === 'multi-window' ? 'white' : 'var(--text-secondary)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: activeTab === 'multi-window' ? 600 : 500,
              fontSize: '13px',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
              gap: '6px',
            }}
          >
            <WindowIcon /> Multi-Window
          </button>
          <button
            onClick={() => setActiveTab('manual')}
            style={{
              height: '36px',
              padding: '0 16px',
              background: activeTab === 'manual' ? 'var(--primary)' : 'transparent',
              color: activeTab === 'manual' ? 'white' : 'var(--text-secondary)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: activeTab === 'manual' ? 600 : 500,
              fontSize: '13px',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
              gap: '6px',
            }}
          >
            <ManualSetupIcon /> Manual Setup
          </button>
          <button
            onClick={() => setActiveTab('about')}
            style={{
              height: '36px',
              padding: '0 16px',
              background: activeTab === 'about' ? 'var(--primary)' : 'transparent',
              color: activeTab === 'about' ? 'white' : 'var(--text-secondary)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: activeTab === 'about' ? 600 : 500,
              fontSize: '13px',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
              gap: '6px',
            }}
          >
            <InfoIcon /> About
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div style={{ marginTop: '8px' }}>
      {/* One-Click Setup Tab */}
      {activeTab === 'one-click' && (
        <div>
          <div className="demo-section">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--accent-1)' }}>
              <OneClickIcon />
              One-Click Demo Setup
            </h3>
            <p>Create all sample servers and groups with a single click:</p>

            <div className="demo-servers-grid">
              {DEMO_SERVERS.map((server) => (
                <div key={server.serverId} className="demo-server-card">
                  <div className="demo-server-header" style={{ borderLeftColor: server.color }}>
                    <h4>{server.name}</h4>
                    <code style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <BuildingIcon />
                      {server.serverId}
                    </code>
                  </div>
                  <div className="demo-groups-list">
                    {server.groups.map((group) => (
                      <div key={group.name} className="demo-group-item">
                        <span className="demo-group-name" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <ChatIcon />
                          {group.name}
                        </span>
                        <span className="demo-group-desc">{group.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <button
              className="btn btn-primary btn-large"
              onClick={handleCreateDemoServers}
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Demo Servers'}
            </button>
          </div>

          {createdServers.length > 0 && (
            <div className="demo-section">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--accent-1)' }}>
                <SuccessIcon />
                Created Servers & Groups
              </h3>
              <div className="demo-created-list">
                {createdServers.map((item) => (
                  <div key={item} className="demo-created-item">
                    <CheckIcon />
                    <code>{item}</code>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Multi-Window Testing Tab */}
      {activeTab === 'multi-window' && (
        <div className="demo-section" style={{ borderLeft: '4px solid #3A8C45' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--accent-1)', margin: '0 0 16px 0' }}>
            <WindowIcon /> Multi-Window Chat Testing
          </h3>
          <p style={{ margin: '0 0 16px 0' }}>
            Test real-time chat with multiple windows side-by-side. Perfect for verifying message delivery across different clients.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <label htmlFor="numWindows" style={{ fontWeight: 500 }}>
              Number of Windows:
            </label>
            <input
              id="numWindows"
              type="number"
              min="1"
              max="10"
              value={numberOfWindows}
              onChange={(e) => setNumberOfWindows(Math.max(1, Math.min(10, parseInt(e.target.value) || 4)))}
              style={{
                width: '80px',
                padding: '8px 12px',
                border: '1px solid rgba(58, 140, 69, 0.3)',
                borderRadius: '6px',
                background: 'rgba(15, 23, 42, 0.6)',
                color: '#f8fafc',
                fontSize: '14px',
              }}
            />
            <button
              onClick={handleOpenMultipleWindows}
              className="button button-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <WindowIcon /> Open Windows
            </button>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><LightbulbIcon /> Tip: Open the app in multiple windows and send messages from one to see them appear in real-time in all other windows.</span>
          </p>
        </div>
      )}

      {/* Manual Setup Tab */}
      {activeTab === 'manual' && (
        <div className="demo-section">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--accent-1)' }}>
            <ManualSetupIcon />
            Manual Setup with curl
          </h3>
          <p>Create servers manually using these curl commands:</p>

          {DEMO_SERVERS.map((server) => (
            <div key={server.serverId} className="demo-curl-section">
              <h4>{server.name}</h4>
              {server.groups.map((group) => {
                const curlCommand = `curl -X POST ${apiBaseUrl}/api/edge-stream/groups/create \\
  -H "Content-Type: application/json" \\
  -k \\
  -d '{
    "data": {
      "serverId": "${server.serverId}",
      "groupName": "${group.name}",
      "description": "${group.description}",
      "createdBy": "demo"
    }
  }'`;

                return (
                  <div key={`${server.serverId}-${group.name}`} className="demo-curl-command">
                    <div className="demo-curl-label">
                      {server.serverId} / {group.name}
                    </div>
                    <div className="demo-curl-code">
                      <code>{curlCommand}</code>
                      <button
                        className="demo-copy-btn"
                        onClick={() => copyToClipboard(curlCommand)}
                        title="Copy to clipboard"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                      >
                        <CopyIcon />
                        Copy
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* About Tab */}
      {activeTab === 'about' && (
        <div className="demo-section demo-info" style={{ marginTop: '20px' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--accent-1)' }}>
            <InfoIcon />
            About Demo Mode
          </h3>
          <ul>
            <li>Demo servers are created in memory and will persist until the API server restarts</li>
            <li>Groups are automatically organized by server for easy testing</li>
            <li>Use the Servers tab to view all created servers and groups</li>
            <li>You can create additional groups within any server using the "Create Group" button</li>
            <li>All groups are visible to all connected clients in real-time</li>
          </ul>
          </div>
        )}
      </div>
      </div>
    </ErrorBoundary>
  );
}
